<template>
    <div class="team-anim flex flex-col items-center gap-7.5 pt-3 pb-2">
        <div ref="stage" class="ta-stage" aria-hidden="true">
            <div ref="inner" class="ta-inner">
                <svg v-for="tri in TRIANGLES" :key="`${tri.row}-${tri.col}`" class="ta-tri" viewBox="0 0 38 30" :style="triBaseStyle(tri)">
                    <polygon points="1.23,1.23 36.77,1.23 19,28.77" :fill="tri.fill" :stroke="tri.fill" />
                </svg>
            </div>
        </div>
        <p class="body-small text-valence-stone-500 animate-pulse motion-reduce:animate-none" role="status" aria-live="polite">Sending your feedback…</p>
    </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, useTemplateRef } from "vue";

const emit = defineEmits<{ done: [] }>();

// Same mint pair as ReflectionIcon's `team` mark, which this animation folds back into.
const LIGHT = "#BEE59F";
const DARK = "#5FA37C";
const COL_GAP = 46;
const ROW_GAP = 42;
const FOLD_DEGREES = 158;
const FOLD_DURATION = 480;
const OUT_DURATION = Math.round(FOLD_DURATION * 1.7);
const STEP = 90;
const HOLD = 520;
const FOLD_START = 140;
const BACK_START = FOLD_START + 2 * STEP + OUT_DURATION + HOLD;
const TWIRL_AT = BACK_START + 2 * STEP + FOLD_DURATION + 180;

interface Triangle {
    row: number;
    col: number;
    fill: string;
    isMark: boolean;
}

const TRIANGLES: Triangle[] = Array.from({ length: 9 }, (_, index) => {
    const row = Math.floor(index / 3);
    const col = index % 3;
    return { row, col, fill: (row + col) % 2 === 0 ? LIGHT : DARK, isMark: col === 1 && row < 2 };
});

const stage = useTemplateRef<HTMLElement>("stage");
const inner = useTemplateRef<HTMLElement>("inner");
const timers: ReturnType<typeof setTimeout>[] = [];

function offsetX(tri: Triangle): number {
    return (tri.col - 1) * COL_GAP;
}

function offsetY(tri: Triangle): number {
    return (tri.row - 1) * ROW_GAP;
}

function cellTransform(tri: Triangle): string {
    return `translate(${offsetX(tri)}px, ${offsetY(tri)}px) rotateX(0deg) rotateY(0deg)`;
}

function homeTransform(tri: Triangle): string {
    return `translate(0px, ${tri.fill === DARK ? -10 : 10}px) rotateX(0deg) rotateY(0deg)`;
}

function foldedTransform(tri: Triangle): string {
    const ry = tri.col === 0 ? FOLD_DEGREES : tri.col === 2 ? -FOLD_DEGREES : 0;
    const rx = tri.row === 0 ? -FOLD_DEGREES : tri.row === 2 ? FOLD_DEGREES : 0;
    return `translate(${offsetX(tri)}px, ${offsetY(tri)}px) rotateX(${rx}deg) rotateY(${ry}deg)`;
}

function shutTransform(tri: Triangle): string {
    const ry = tri.col === 0 ? 90 : tri.col === 2 ? -90 : 0;
    const rx = tri.row === 0 ? -90 : tri.row === 2 ? 90 : 0;
    return `translate(0px, 0px) rotateX(${rx}deg) rotateY(${ry}deg) scale(0.35)`;
}

function triBaseStyle(tri: Triangle): Record<string, string> {
    const originX = tri.col === 0 ? "100%" : tri.col === 2 ? "0%" : "50%";
    const originY = tri.row === 0 ? "100%" : tri.row === 2 ? "0%" : "50%";
    return {
        transformOrigin: `${originX} ${originY}`,
        transform: tri.isMark ? homeTransform(tri) : foldedTransform(tri),
        opacity: tri.isMark ? "1" : "0",
    };
}

// Distance from the centre cell, so the fold ripples outwards.
function ripple(tri: Triangle): number {
    return Math.abs(tri.row - 1) + Math.abs(tri.col - 1);
}

function at(delay: number, run: () => void) {
    timers.push(setTimeout(run, delay));
}

onMounted(() => {
    const elements = [...(stage.value?.querySelectorAll<HTMLElement>(".ta-tri") ?? [])];

    if (globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        at(500, () => emit("done"));
        return;
    }

    TRIANGLES.forEach((tri, index) => {
        const element = elements[index];
        if (!element) {
            return;
        }

        at(FOLD_START + ripple(tri) * STEP, () => {
            element.style.transition = `transform ${OUT_DURATION}ms cubic-bezier(.3,.8,.35,1), opacity ${Math.round(OUT_DURATION * 0.32)}ms ease`;
            element.style.opacity = "1";
            element.style.transform = cellTransform(tri);
        });

        at(BACK_START + (2 - ripple(tri)) * STEP, () => {
            if (tri.isMark) {
                element.style.transition = `transform ${FOLD_DURATION}ms cubic-bezier(.4,0,.35,1)`;
                element.style.transform = homeTransform(tri);
                return;
            }
            element.style.transition = `transform ${FOLD_DURATION}ms cubic-bezier(.5,0,.5,1)`;
            element.style.transform = shutTransform(tri);
            at(BACK_START + (2 - ripple(tri)) * STEP + FOLD_DURATION, () => {
                element.style.transition = "none";
                element.style.opacity = "0";
            });
        });
    });

    at(TWIRL_AT, () => {
        if (inner.value) {
            inner.value.style.transition = "transform 620ms cubic-bezier(.5,0,.2,1)";
            inner.value.style.transform = "rotate(360deg)";
        }
    });
    at(TWIRL_AT + 700, () => emit("done"));
});

onBeforeUnmount(() => timers.forEach(clearTimeout));
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

.ta-stage {
    position: relative;
    width: 220px;
    height: 200px;
    perspective: 520px;
    perspective-origin: 50% 50%;
}

.ta-inner {
    position: absolute;
    inset: 0;
    transform-style: preserve-3d;
    transform: rotate(180deg);
    will-change: transform;
}

.ta-tri {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 38px;
    height: 30px;
    margin: -15px 0 0 -19px;
    overflow: visible;
    transform-style: preserve-3d;
    will-change: transform, opacity;
}

.ta-tri polygon {
    stroke-width: 2.46px;
    stroke-linejoin: round;
}
</style>
